import {useI18n} from '@shopify/react-i18n';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useToast} from 'utilities/toasts';
import {useMutation, useQuery} from '@shopify/react-graphql';
import {useShop} from '@web-utilities/shop';
import {useMonorail} from 'packages/monorail';
import {parseGidType} from '@shopify/admin-graphql-api-utilities';
import {useSessionId} from '@web-utilities/session-id';
import {useSetShowFeedbackCardDate} from 'utilities/use-set-show-feedback-card-date';
import {
  ShopLocale,
  useShopLocalesAndMarkets,
} from 'utilities/use-shop-locales-and-markets';

import ShopMachineTranslationBatchesQuery, {
  ShopMachineTranslationBatchesQueryData,
} from './graphql/ShopMachineTranslationBatchesQuery.graphql';
import MachineTranslationBatchesInProgressQuery, {
  MachineTranslationBatchesInProgressQueryData,
} from './graphql/MachineTranslationBatchesInProgressQuery.graphql';
import MachineTranslateResourcesMutation from './graphql/MachineTranslateResourcesMutation.graphql';
import MachineTranslateShopMutation from './graphql/MachineTranslateShopMutation.graphql';
import {ConfirmMachineTranslateModal} from './components';

const ERROR_SEPARATOR = ' • ';
export class UserError extends Error {}

const POLLING_INTERVAL = 5000;

export interface MachineTranslateOptions {
  /** Name of the resource being fetched or machine-translated. Eg: Spanish or Awesome Table */
  resourceName: string;
  /** Use specific locale. Leave empty to use locale from global app state */
  locale?: ShopLocale;
  /** Use Machine Translation for specific resources. Leave empty for all resources under the locale  */
  resourceIds?: string[];
  /** Set to true to skip polling MT for this resource  */
  skip?: boolean;
}

interface Return {
  ConfirmMachineTranslateModal: FunctionComponent;
  confirmMachineTranslate(): void;
  enabled: boolean;
  initialized: boolean;
  lastBatch?: ShopMachineTranslationBatchesQueryData.ShopMachineTranslationBatches;
  inProgressBatch?: MachineTranslationBatchesInProgressQueryData.MachineTranslationBatchesInProgress;
}

export function useMachineTranslate(options: MachineTranslateOptions): Return {
  const {resourceName, resourceIds = undefined, skip = false} = options;
  const monorail = useMonorail();
  const sessionId = useSessionId();
  const [i18n] = useI18n();
  const toast = useToast();
  const setShowFeedbackCardDate = useSetShowFeedbackCardDate();
  const {
    machineTranslationEnabled,
    machineTranslationLocaleLimit,
    machineTranslationCustomCredentialsEnabled,
  } = useShop();
  const {
    currentLocale: currentShopLocale,
    locales,
    loading: shopLocalesLoading,
  } = useShopLocalesAndMarkets();
  const [open, setOpen] = useState(false);
  const resolveRef = useRef<(confirmed: boolean) => void>();

  const shopLocale = useMemo(
    () => options.locale || currentShopLocale,
    [currentShopLocale, options.locale],
  );
  const locale = useMemo(() => shopLocale?.locale || '', [shopLocale?.locale]);
  const machineTranslatedLocales = useMemo(
    () => locales.filter((locale) => locale.machineTranslated),
    [locales],
  );

  const machineTranslateResources = useMutation(
    MachineTranslateResourcesMutation,
  );
  const machineTranslateShop = useMutation(MachineTranslateShopMutation);

  const shouldSkip = useMemo(
    () =>
      skip ||
      shopLocalesLoading ||
      !machineTranslationEnabled ||
      !shopLocale ||
      currentShopLocale?.primary,
    [
      skip,
      shopLocalesLoading,
      machineTranslationEnabled,
      shopLocale,
      currentShopLocale?.primary,
    ],
  );

  const {
    data: inProgressBatchesData,
    loading,
    startPolling,
    stopPolling,
  } = useQuery(MachineTranslationBatchesInProgressQuery, {
    variables: {
      resourceIds,
    },
    fetchPolicy: 'network-only',
    skip: shouldSkip,
    onError: () => {
      toast.show({
        content: i18n.translate('Common.serverError'),
        error: true,
      });
    },
  });

  const {data: batchesData, loading: shopMachineTranslationBatchesLoading} =
    useQuery(ShopMachineTranslationBatchesQuery, {
      fetchPolicy: 'network-only',
      skip: shouldSkip,
      onError: () => {
        toast.show({
          content: i18n.translate('Common.serverError'),
          error: true,
        });
      },
    });

  const initialized = useMemo(
    () =>
      shouldSkip ||
      (!loading &&
        !shopMachineTranslationBatchesLoading &&
        Boolean(batchesData?.shopMachineTranslationBatches) &&
        Boolean(inProgressBatchesData?.machineTranslationBatchesInProgress)),
    [
      batchesData?.shopMachineTranslationBatches,
      inProgressBatchesData?.machineTranslationBatchesInProgress,
      loading,
      shopMachineTranslationBatchesLoading,
      shouldSkip,
    ],
  );

  const lastBatch = useMemo(
    () =>
      batchesData?.shopMachineTranslationBatches.find(
        (batch) => batch.locale === locale,
      ),
    [locale, batchesData?.shopMachineTranslationBatches],
  );

  const inProgressBatch = useMemo(
    () =>
      // We want to filter out inProgressBatches for the HomePage when the batch is a resource batch and the batch locale is not the current locale
      // If there are multiple inProgressBatches we want to prioritize the current locale batch
      inProgressBatchesData?.machineTranslationBatchesInProgress
        .filter(
          (batch) =>
            resourceIds !== undefined ||
            batch.resourceId === null ||
            batch.locale === locale,
        )
        .sort((batch) => (batch.locale === locale ? -1 : 1))[0],
    [
      inProgressBatchesData?.machineTranslationBatchesInProgress,
      locale,
      resourceIds,
    ],
  );

  useEffect(() => {
    if (
      !shopLocalesLoading &&
      inProgressBatchesData &&
      inProgressBatchesData.machineTranslationBatchesInProgress.length !== 0
    ) {
      startPolling(POLLING_INTERVAL);
    } else {
      stopPolling();
    }
  }, [
    inProgressBatchesData,
    locale,
    resourceIds,
    shopLocalesLoading,
    startPolling,
    stopPolling,
  ]);

  /** Reset/Un-initialize the hook when on mount or when resourceIds change */
  useEffect(() => {
    stopPolling();
    startPolling(POLLING_INTERVAL);
  }, [resourceIds, startPolling, stopPolling]);

  /** Stop polling when hook unmounts */
  useEffect(() => {
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const produceAutoTranslateModalViewedMonorailEvent = useCallback(() => {
    monorail.produce({
      schemaId: 'machine_translations_event/1.0',
      payload: {
        eventType: 'auto_translate_modal_viewed',
        resourceLocale: locale,
        resourceType: resourceIds ? parseGidType(resourceIds[0]) : undefined,
        resourceName: resourceIds ? resourceName : undefined,
        sessionToken: sessionId,
      },
    });
  }, [locale, monorail, resourceIds, resourceName, sessionId]);

  const produceAutoTranslateActionMonorailEvent = useCallback(() => {
    monorail.produce({
      schemaId: 'machine_translations_event/1.0',
      payload: {
        eventType: 'auto_translate',
        resourceLocale: locale,
        resourceType: resourceIds ? parseGidType(resourceIds[0]) : undefined,
        resourceName: resourceIds ? resourceName : undefined,
        sessionToken: sessionId,
      },
    });
  }, [locale, monorail, resourceIds, resourceName, sessionId]);

  const machineTranslate = useCallback(async () => {
    if (!locale) return;

    try {
      const mutation = resourceIds
        ? async () => {
            const {data} = await machineTranslateResources({
              variables: {locale, resourceIds: options.resourceIds as string[]},
              awaitRefetchQueries: true,
              refetchQueries: [
                'ShopLocales',
                'MachineTranslationBatchesInProgress',
                'ShopMachineTranslationBatches',
              ],
            });

            if (data?.machineTranslate.userErrors.length) {
              throw new UserError(
                data.machineTranslate.userErrors
                  .map(({message}) => message)
                  .join(ERROR_SEPARATOR),
              );
            }
          }
        : async () => {
            const {data} = await machineTranslateShop({
              variables: {locale},
              awaitRefetchQueries: true,
              refetchQueries: [
                'ShopLocales',
                'MachineTranslationBatchesInProgress',
                'ShopMachineTranslationBatches',
              ],
            });

            if (data?.machineTranslateShop.userErrors.length) {
              throw new UserError(
                data.machineTranslateShop.userErrors
                  .map(({message}) => message)
                  .join(ERROR_SEPARATOR),
              );
            }
          };
      produceAutoTranslateActionMonorailEvent();
      setShowFeedbackCardDate();
      await mutation();

      startPolling(POLLING_INTERVAL);
    } catch (error) {
      if (error instanceof UserError) {
        toast.show({
          content: error.message,
          error: true,
          duration: error.message.split(ERROR_SEPARATOR).length * 3000,
        });
        return;
      }

      toast.show({
        content: i18n.translate('Common.serverError'),
        error: true,
      });
    }
  }, [
    locale,
    resourceIds,
    machineTranslateResources,
    options.resourceIds,
    machineTranslateShop,
    produceAutoTranslateActionMonorailEvent,
    startPolling,
    toast,
    i18n,
    setShowFeedbackCardDate,
  ]);

  const confirmMachineTranslate = useCallback(async () => {
    setOpen(true);
    produceAutoTranslateModalViewedMonorailEvent();
    const confirmed = await new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
    setOpen(false);

    if (confirmed) {
      machineTranslate();
    }

    return confirmed;
  }, [machineTranslate, produceAutoTranslateModalViewedMonorailEvent]);

  const renderModal = useCallback<Return['ConfirmMachineTranslateModal']>(
    () =>
      shopLocale ? (
        <ConfirmMachineTranslateModal
          open={open}
          resourceName={resourceName}
          shopLocale={shopLocale}
          wholeShop={!resourceIds}
          machineTranslatedLocales={machineTranslatedLocales}
          machineTranslationLocaleLimit={machineTranslationLocaleLimit}
          machineTranslationCustomCredentialsEnabled={
            machineTranslationCustomCredentialsEnabled
          }
          onCancel={() => {
            if (resolveRef.current) resolveRef.current(false);
            // should never be able to get here without a resolver
            // but if we do just close the modal
            else setOpen(false);
          }}
          onConfirm={() => {
            if (resolveRef.current) resolveRef.current(true);
            // should never be able to get here without a resolver
            // but if we do just close the modal
            else setOpen(false);
          }}
        />
      ) : null,
    [
      open,
      resourceName,
      shopLocale,
      resourceIds,
      machineTranslatedLocales,
      machineTranslationLocaleLimit,
      machineTranslationCustomCredentialsEnabled,
    ],
  );

  return {
    ConfirmMachineTranslateModal: renderModal,
    confirmMachineTranslate,
    inProgressBatch,
    lastBatch,
    initialized,
    enabled: machineTranslationEnabled,
  };
}
